import React, { ReactNode } from 'react';

import Span from 'components/elements/span';
import Box from 'components/elements/Box';
import AnimatedCaret from 'components/base/AnimatedCaret';
import colors from 'styles/themes/colors';
import styled, { css } from 'styled-components/macro';
import { ThemeProps } from 'types/styled';
import variables from 'styles/themes/variables';
import Menu from 'components/base/Menu/Menu';
import { Link } from 'react-router-dom';
import modularScale from 'styles/helpers/modular-scale';

const LayoutWrapper = styled.div`
  [data-menu-button] {
    display: block;
    letter-spacing: 1.11111px;
    line-height: 1.6;
  }
`;

interface IManageListItem {
  label: string;
  link?: string;
  action?: () => void;
}

const ListItemBox = styled(Box)<ThemeProps<'div'>>`
  min-width: 10em;
  ${({ isActive }): any => css`
    cursor: pointer;
    background: ${isActive ? colors.Sand : 'white'};

    span {
      display: inline-block;
      transition: ${`150ms ${variables.timingFunction} transform`};
    }

    &:hover,
    &[aria-selected='true'] {
      background: ${colors.Sand};
      span {
        transform: ${!isActive && 'translateX(5px)'};
      }
    }
  `}
`;

const Button: React.FC<IButtonProps> = (props) => {
  const { selectedItem, isOpen, disabled, defaultLabel } = props;

  const boxColor = disabled ? colors.Grey : colors.White;

  return (
    <Box
      bg="transparent"
      display="flex"
      alignItems="flex-start"
      justifyContent="flex-start"
      alignContent="stretch"
      color={boxColor}
      px={3}
      py={1}
      width="auto"
      fontSize={modularScale(-2)}
      borderWidth="1px"
      borderStyle="solid"
      borderColor={colors.White}
      borderRadius="0.2em"
    >
      <Span
        alignSelf="auto"
        marginRight={3}
        fontWeight="600"
        textTransform="uppercase"
      >
        {selectedItem?.label || defaultLabel}
      </Span>
      <Span alignSelf="auto">
        <AnimatedCaret isPointingUp={isOpen} />
      </Span>
    </Box>
  );
};

const ListItem: React.FC<IListItemProps> = (props) => {
  const { item, isActive } = props;

  return (
    <>
      {item.link && (
        <Link to={item.link}>
          <ListItemBox
            isActive={isActive}
            display="flex"
            alignItems="flex-start"
            justifyContent="flex-start"
            alignContent="stretch"
            paddingRight={2}
            paddingLeft={3}
            py={2}
            color={colors.DeepBlue}
          >
            <Span marginLeft={1} alignSelf="auto">
              {item.label}
            </Span>
          </ListItemBox>
        </Link>
      )}
      {!item.link && (
        <ListItemBox
          isActive={isActive}
          display="flex"
          alignItems="flex-start"
          justifyContent="flex-start"
          alignContent="stretch"
          paddingRight={2}
          paddingLeft={3}
          py={2}
          color={colors.DeepBlue}
        >
          <Span marginLeft={1} alignSelf="auto">
            {item.label}
          </Span>
        </ListItemBox>
      )}
    </>
  );
};

const getButtonWithDefaultLabel = (defaultLabel?: string): ReactNode => {
  const LabeledButton: React.FC<any> = (props: any) => {
    return <Button {...props} defaultLabel={defaultLabel} />;
  };
  return LabeledButton;
};

const ManageListDropdown: React.FC<IManageListDropdownProps> = (props) => {
  const { items, selectedItem, disabled, itemToString, defaultLabel } = props;

  const onLocalChange = (item: IManageListItem): void => {
    if (item.action) {
      item.action();
    }
  };

  // selectedItem can be undefined which will trigger React into thinking this is a controlled component,
  // when it then switches to an actual value runtime errors will be thrown since React will figure
  // we're trying to switch a conrtolled component into an uncontrolled component. Null is a valid value,
  // therefore we add this seemingly weird check.
  return (
    <LayoutWrapper data-pw="manage-custom-lists-dropdown">
      <Menu
        onChange={onLocalChange}
        items={items}
        selectedItem={selectedItem || null}
        disabled={disabled}
        itemToString={itemToString}
        Button={getButtonWithDefaultLabel(defaultLabel)}
        Item={ListItem}
      />
    </LayoutWrapper>
  );
};

interface IManageListDropdownProps {
  items: IManageListItem[];
  selectedItem?: IManageListItem;
  disabled?: boolean;
  itemToString?: (item: any) => string | null;
  defaultLabel?: string;
}

interface IButtonProps {
  selectedItem: IManageListItem;
  isOpen?: boolean;
  disabled?: boolean;
  defaultLabel?: string;
}

interface IListItemProps {
  item: IManageListItem;
  isActive?: boolean;
}

export default ManageListDropdown;
